/**
 * What "held up" means, once, for every surface that says it.
 *
 * A claim held up when something outside the model's reasoning put it to a test
 * and it came back on the claim's side. Two records can say so, and only two:
 *
 *  - `result` — a result somebody recorded went the way the claim needed. The
 *    calibration ledger already called these "held up" ("6 of 6 tested claims
 *    held up"), and this is that word, not a second one.
 *  - `sources` — where no result settled it, a live source check read its
 *    sources as appearing to agree. Weaker, and said as such wherever it is
 *    printed: a reading is what one search returned on a date, which is why the
 *    checked ledger says a claim scored as holding up "has not been shown to be
 *    true".
 *
 * A recorded result outranks a reading, in both directions, exactly as it
 * outranks an ending (`endingOf`): a result against the claim means it did not
 * hold up whatever its check read, and an inconclusive one settles nothing and
 * leaves the reading to speak.
 *
 * WHAT IS NOT HERE, deliberately, is the mirror of what "didn't survive" takes
 * in. An objection that stood is the model's judgement and the report files it
 * as not surviving; the opposite — an objection raised that did not stand, a
 * cross-examination that found no specific objection — is not a record of the
 * claim holding, because every claim nobody has knocked down yet reads the same
 * way. The account already says so of the review ("a fact about one attempt to
 * break them, not a verdict"), and a heading saying "held up" over it would be
 * the tool upgrading an absence into a finding. So the asymmetry is the rule: the
 * app is quick to say what broke, and says what held only over a record from
 * outside the model.
 *
 * Read off stored fields only, like `endingOf`, so the report's section, the
 * run's end card and anything later that counts it word one claim one way.
 */

import type { ReasonNode, SourceReading } from './types'
import { verificationLive } from './types'

export type HeldUpBy = 'result' | 'sources'

/** The one reading that counts as holding up. A reading, not a node: a run's
 *  end card reads the reading its own check recorded (`core/runFound.ts`). */
export const readingHeldUp = (r: SourceReading): boolean => r === 'supported'

/** Which record says this claim held up, or null for none. */
export function heldUpBy(n: ReasonNode): HeldUpBy | null {
  const o = n.outcome
  if (o && o.bearing !== 'inconclusive') return o.bearing === 'supported' ? 'result' : null
  const v = n.verification
  return v && readingHeldUp(v.reading) && verificationLive(n) ? 'sources' : null
}
